import React, { useState, useEffect, useCallback } from 'react';
import { ccmApi } from '../lib/api.js';
import { planProviderPhoneSave } from '../lib/provider-phone-save.js';

const STATE_LABELS = {
  awaiting_patient: 'Awaiting patient',
  awaiting_selection: 'Awaiting selection',
  awaiting_med_details: 'Awaiting dose & frequency',
  awaiting_confirmation: 'Awaiting confirm',
  undo_window: 'Undo window',
  completed: 'Completed',
  cancelled: 'Cancelled',
  expired: 'Expired',
};

const STATE_COLORS = {
  completed: '#1a7f4b',
  cancelled: '#666',
  expired: '#888',
  undo_window: '#b45309',
  awaiting_confirmation: '#1d4ed8',
  awaiting_patient: '#1d4ed8',
  awaiting_selection: '#1d4ed8',
  awaiting_med_details: '#1d4ed8',
};

function orderSummary(orders) {
  if (!orders || !orders.length) return '—';
  return orders.map(o => {
    if (o.type === 'lab') return `Lab: ${(o.tests || []).join(', ')}`;
    if (o.type === 'imaging') return `Imaging: ${(o.tests || []).join(', ')}`;
    if (o.type === 'referral') return `Referral: ${o.specialty}`;
    if (o.type === 'cardiac') return `Cardiac: ${(o.tests || []).join(', ')}`;
    if (o.type === 'pulmonary') return `Pulmonary: ${(o.tests || []).join(', ')}`;
    if (o.type === 'sleep') return `Sleep: ${(o.tests || []).join(', ')}`;
    if (o.type === 'medication') return `Rx: ${o.drug_name} ${o.dose || ''}`.trim();
    return o.type;
  }).join(' · ');
}

function formatTime(ts) {
  if (!ts) return '—';
  const d = new Date(ts);
  return d.toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

function formatLabSyncStatus(status) {
  if (!status || status.status === 'idle') return null;
  if (status.status === 'running') {
    const copied = status.total || 0;
    const compendiums = Array.isArray(status.compendiums) ? status.compendiums.length : 0;
    const index = Number.isFinite(Number(status.currentIndex)) ? Number(status.currentIndex) : 0;
    const where = compendiums ? ` (${Math.min(index + 1, compendiums)} of ${compendiums})` : '';
    return `Syncing lab catalog: ${copied} tests copied${where}.`;
  }
  if (status.status === 'succeeded') {
    const orderSets = Number(status.orderSetCount || 0);
    const setText = orderSets ? ` and ${orderSets} order sets` : '';
    const setError = status.orderSetError ? ` Order set sync failed: ${status.orderSetError}.` : '';
    const comps = Array.isArray(status.results) ? status.results.length : 0;
    const compText = comps ? ` across ${comps} compendium${comps === 1 ? '' : 's'}` : '';
    // Counted, not named: the crawl covers every connected vendor this
    // practice has, which has not been just Quest and LabCorp for some time.
    return `Synced ${status.total || 0} lab tests${setText}${compText}.${setError}`;
  }
  if (status.status === 'failed') return `Sync failed: ${status.error || 'unknown error'}`;
  return null;
}

function SessionDetailModal({ sessionId, onClose }) {
  const [session, setSession] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    ccmApi.getSmsSession(sessionId)
      .then(setSession)
      .catch(e => setError(e.message));
  }, [sessionId]);

  function buildThread(s) {
    const turns = [];
    turns.push({ from: 'provider', text: s.rawMessage, time: s.createdAt });

    if (s.candidatePatients && s.candidatePatients.length > 1) {
      const opts = s.candidatePatients
        .map((p, i) => `${i + 1} for ${p.firstName} ${p.lastName}`)
        .join(' or ');
      turns.push({ from: 'bot', text: `Found ${s.candidatePatients.length} patients. Reply ${opts}.` });
    }

    if (s.patientName && s.extractedOrders && s.extractedOrders.length > 0) {
      const orders = orderSummary(s.extractedOrders);
      turns.push({ from: 'bot', text: `${s.patientName}. ${orders}. Reply yes to send or no to cancel.` });
    }

    if (s.state === 'completed' || s.state === 'undo_window') {
      turns.push({ from: 'provider', text: 'yes' });
      turns.push({ from: 'bot', text: 'Sending in 30 seconds. Reply undo to stop.' });
    }
    if (s.state === 'cancelled') {
      turns.push({ from: 'provider', text: 'no / undo' });
      turns.push({ from: 'bot', text: 'Order cancelled.' });
    }
    if (s.state === 'completed' && s.elationOrderIds && s.elationOrderIds.length > 0) {
      const count = s.elationOrderIds.length;
      turns.push({ from: 'bot', text: `Done. ${count} order${count > 1 ? 's' : ''} sent to Elation, ready for your signature.` });
    }
    return turns;
  }

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div
        className="modal"
        role="dialog"
        aria-modal="true"
        onClick={e => e.stopPropagation()}
        style={{ maxWidth: 520 }}
      >
        <div className="modal-head" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3 style={{ margin: 0 }}>SMS Session</h3>
          <button className="btn" onClick={onClose} style={{ padding: '4px 10px' }}>✕</button>
        </div>
        <div className="modal-body" style={{ padding: '16px 20px' }}>
          {error && <p style={{ color: 'red' }}>{error}</p>}
          {!session && !error && <p style={{ color: '#888' }}>Loading…</p>}
          {session && (
            <>
              <div style={{ marginBottom: 14, display: 'flex', gap: 12, flexWrap: 'wrap', fontSize: 13, color: '#555' }}>
                <span>Provider: {session.providerPhone}</span>
                {session.patientName && <span>Patient: {session.patientName}</span>}
                <span style={{ color: STATE_COLORS[session.state] || '#333', fontWeight: 600 }}>
                  {STATE_LABELS[session.state] || session.state}
                </span>
                <span>{formatTime(session.createdAt)}</span>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {buildThread(session).map((turn, i) => (
                  <div
                    key={i}
                    style={{
                      alignSelf: turn.from === 'provider' ? 'flex-end' : 'flex-start',
                      maxWidth: '80%',
                      background: turn.from === 'provider' ? '#dbeafe' : '#f3f4f6',
                      borderRadius: 10,
                      padding: '8px 12px',
                      fontSize: 14,
                    }}
                  >
                    <div style={{ fontSize: 11, color: '#888', marginBottom: 2 }}>
                      {turn.from === 'provider' ? 'Provider' : 'Bot'}
                      {turn.time ? ` · ${formatTime(turn.time)}` : ''}
                    </div>
                    {turn.text}
                  </div>
                ))}
              </div>

              {session.elationOrderIds && session.elationOrderIds.length > 0 && (
                <div style={{ marginTop: 14, padding: '10px 12px', background: '#f0fdf4', borderRadius: 6, fontSize: 13 }}>
                  <strong>Orders created in Elation:</strong>
                  <ul style={{ margin: '6px 0 0', paddingLeft: 16 }}>
                    {session.elationOrderIds.map((o, i) => (
                      <li key={i}>{o.type} order (ID: {o.elationId})</li>
                    ))}
                  </ul>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function DemoChat() {
  const [turns, setTurns] = useState([
    { from: 'bot', text: 'Demo bot ready. Try: "CBC and BMP for <a patient name>". Reply yes to send (creates a real draft order in Elation) or no to cancel.' },
  ]);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);

  async function send() {
    const message = input.trim();
    if (!message || busy) return;
    setInput('');
    setTurns(t => [...t, { from: 'provider', text: message }]);
    setBusy(true);
    try {
      const { replies } = await ccmApi.runSmsDemo(message);
      setTurns(t => [...t, ...(replies || []).map(text => ({ from: 'bot', text }))]);
    } catch (e) {
      setTurns(t => [...t, { from: 'bot', text: `Error: ${e.message}`, error: true }]);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div style={{ maxWidth: 560, margin: '0 auto' }}>
      <div style={{
        padding: '10px 14px', background: '#fffbeb', border: '1px solid #fde68a',
        borderRadius: 8, fontSize: 13, color: '#92400e', marginBottom: 14,
      }}>
        This is a live demo of the SMS order bot. It runs the same logic as a real text
        message and <strong>creates real draft orders in Elation</strong> on "yes"
        (unsigned — a provider must still sign them). In real SMS there's a 30-second
        undo window; the demo creates immediately.
      </div>

      <div style={{
        border: '1px solid #e5e7eb', borderRadius: 10, padding: 14,
        minHeight: 280, display: 'flex', flexDirection: 'column', gap: 8,
        background: '#fafafa',
      }}>
        {turns.map((turn, i) => (
          <div key={i} style={{
            alignSelf: turn.from === 'provider' ? 'flex-end' : 'flex-start',
            maxWidth: '80%',
            background: turn.error ? '#fee2e2' : turn.from === 'provider' ? '#dbeafe' : '#fff',
            border: turn.from === 'bot' && !turn.error ? '1px solid #e5e7eb' : 'none',
            borderRadius: 12, padding: '8px 12px', fontSize: 14,
            color: turn.error ? '#991b1b' : '#111',
          }}>
            <div style={{ fontSize: 11, color: '#999', marginBottom: 2 }}>
              {turn.from === 'provider' ? 'You' : 'Bot'}
            </div>
            {turn.text}
          </div>
        ))}
        {busy && <div style={{ alignSelf: 'flex-start', color: '#888', fontSize: 13 }}>Bot is thinking…</div>}
      </div>

      <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
        <input
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') send(); }}
          placeholder="Type a message…"
          disabled={busy}
          style={{
            flex: 1, padding: '10px 12px', borderRadius: 8,
            border: '1px solid #d1d5db', fontSize: 14,
          }}
        />
        <button className="btn" onClick={send} disabled={busy || !input.trim()}>Send</button>
      </div>

      <div style={{ marginTop: 10, fontSize: 12, color: '#888' }}>
        Quick replies: <code>yes</code> · <code>no</code> · <code>undo</code> · <code>help</code>
      </div>
    </div>
  );
}

function ProviderMapping() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [savingId, setSavingId] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [syncMsg, setSyncMsg] = useState(null);
  const [providerPhones, setProviderPhones] = useState([]);
  const [phoneDrafts, setPhoneDrafts] = useState({});
  const [phoneSavingId, setPhoneSavingId] = useState(null);
  const [phoneMsgs, setPhoneMsgs] = useState({});

  const load = useCallback(async () => {
    try {
      setData(await ccmApi.getSmsProviderMapping());
      setError(null);
    } catch (e) { setError(e.message); }
    try {
      const cfg = await ccmApi.getSmsConfig();
      if (cfg && cfg.defaults) {
        setDefaults({
          defaultMedQty: cfg.defaults.defaultMedQty ?? '',
          defaultMedRefills: cfg.defaults.defaultMedRefills ?? '',
          applyDefaultQtyToRefills: !!cfg.defaults.applyDefaultQtyToRefills,
          applyDefaultRefillsToRefills: !!cfg.defaults.applyDefaultRefillsToRefills,
        });
      }
      if (cfg) {
        setRecognizedVendors(Array.isArray(cfg.recognizedVendors) ? cfg.recognizedVendors : []);
        setDefaultLabVendor(cfg.defaultLabVendor ?? '');
        setJurisdictionOptions(Array.isArray(cfg.specimenJurisdictionOptions) ? cfg.specimenJurisdictionOptions : []);
        setSpecimenJurisdiction(cfg.labSpecimenJurisdiction ?? '');
        setNorthwellCatalogCount(typeof cfg.northwellCatalogCount === 'number' ? cfg.northwellCatalogCount : null);
        const phones = Array.isArray(cfg.providerPhones) ? cfg.providerPhones : [];
        setProviderPhones(phones);
        const drafts = {};
        phones.forEach(p => { drafts[String(p.userId)] = p.phone; });
        setPhoneDrafts(drafts);
      }
    } catch (e) {
      // Non-fatal — defaults UI just shows empty
      console.warn('Failed to load SMS config defaults:', e.message);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  async function setMapping(userId, elationUserId) {
    setSavingId(userId);
    try {
      await ccmApi.setSmsProviderMapping(userId, elationUserId || null);
      await load();
    } catch (e) { setError(e.message); }
    finally { setSavingId(null); }
  }

  async function savePhone(u) {
    const key = String(u.id);
    const currentPhone = (providerPhones.find(p => String(p.userId) === key) || {}).phone || null;
    const plan = planProviderPhoneSave(phoneDrafts[key], currentPhone);

    if (plan.action === 'none') {
      setPhoneMsgs(m => ({ ...m, [key]: { text: 'No change', isError: false } }));
      return;
    }
    if (plan.action === 'invalid') {
      setPhoneMsgs(m => ({ ...m, [key]: { text: 'Use E.164 format, e.g. +15551234567', isError: true } }));
      return;
    }

    setPhoneSavingId(u.id);
    setPhoneMsgs(m => ({ ...m, [key]: null }));
    try {
      if (plan.action === 'remove') {
        await ccmApi.removeSmsProviderPhone(plan.remove);
        setPhoneMsgs(m => ({ ...m, [key]: { text: 'Removed', isError: false } }));
      } else { // 'set'
        await ccmApi.addSmsProviderPhone(plan.add, u.id);
        if (plan.removeOld) await ccmApi.removeSmsProviderPhone(plan.removeOld);
        setPhoneMsgs(m => ({ ...m, [key]: { text: 'Saved', isError: false } }));
      }
      await load();
    } catch (e) {
      setPhoneMsgs(m => ({ ...m, [key]: { text: e.message, isError: true } }));
    } finally {
      setPhoneSavingId(null);
    }
  }

  async function syncDirectory() {
    setSyncing(true);
    setSyncMsg(null);
    try {
      const { count } = await ccmApi.syncSmsPatientDirectory();
      setSyncMsg(`Synced ${count} patients into the directory.`);
    } catch (e) {
      setSyncMsg(`Sync failed: ${e.message}`);
    } finally {
      setSyncing(false);
    }
  }

  const [labSyncing, setLabSyncing] = useState(false);
  const [labSyncMsg, setLabSyncMsg] = useState(null);
  const [defaults, setDefaults] = useState({
    defaultMedQty: '', defaultMedRefills: '',
    applyDefaultQtyToRefills: false, applyDefaultRefillsToRefills: false,
  });
  const [defaultsSaving, setDefaultsSaving] = useState(false);
  const [defaultsMsg, setDefaultsMsg] = useState('');
  const [recognizedVendors, setRecognizedVendors] = useState([]);
  const [defaultLabVendor, setDefaultLabVendor] = useState('');
  const [labVendorSaving, setLabVendorSaving] = useState(false);
  const [labVendorMsg, setLabVendorMsg] = useState('');
  const [jurisdictionOptions, setJurisdictionOptions] = useState([]);
  const [specimenJurisdiction, setSpecimenJurisdiction] = useState('');
  const [jurisdictionSaving, setJurisdictionSaving] = useState(false);
  const [jurisdictionMsg, setJurisdictionMsg] = useState('');

  function applyLabSyncStatus(status) {
    const message = formatLabSyncStatus(status);
    setLabSyncing(status && status.status === 'running');
    if (message) setLabSyncMsg(message);
    return status;
  }

  const refreshLabSyncStatus = useCallback(async () => {
    const { status } = await ccmApi.getSmsLabCatalogSyncStatus();
    return applyLabSyncStatus(status);
  }, []);

  useEffect(() => {
    refreshLabSyncStatus().catch(e => console.warn('Failed to load lab catalog sync status:', e.message));
  }, [refreshLabSyncStatus]);

  useEffect(() => {
    if (!labSyncing) return undefined;
    let cancelled = false;
    let timer = null;
    const tick = async () => {
      try {
        const { status } = await ccmApi.advanceSmsLabCatalogSync();
        const next = applyLabSyncStatus(status);
        if (!cancelled && next && next.status === 'running') {
          timer = window.setTimeout(tick, 1000);
        }
      } catch (e) {
        if (!cancelled) {
          setLabSyncMsg(`Sync failed: ${e.message}`);
          setLabSyncing(false);
        }
      }
    };
    timer = window.setTimeout(tick, 500);
    return () => {
      cancelled = true;
      if (timer) window.clearTimeout(timer);
    };
  }, [labSyncing]);

  async function syncLabCatalog() {
    setLabSyncing(true);
    setLabSyncMsg('Lab catalog sync is starting.');
    try {
      const { status } = await ccmApi.syncSmsLabCatalog();
      applyLabSyncStatus(status);
    } catch (e) {
      setLabSyncMsg(`Sync failed: ${e.message}`);
      setLabSyncing(false);
    }
  }

  const [orderSetSyncing, setOrderSetSyncing] = useState(false);
  const [orderSetSyncMsg, setOrderSetSyncMsg] = useState(null);
  const [nwSeeding, setNwSeeding] = useState(false);
  const [nwSeedMsg, setNwSeedMsg] = useState(null);
  const [northwellCatalogCount, setNorthwellCatalogCount] = useState(null);
  async function syncLabOrderSets() {
    setOrderSetSyncing(true);
    setOrderSetSyncMsg(null);
    try {
      const { count } = await ccmApi.syncSmsLabOrderSets();
      setOrderSetSyncMsg(`Synced ${count} lab order set${count === 1 ? '' : 's'}.`);
    } catch (e) {
      setOrderSetSyncMsg(`Sync failed: ${e.message}`);
    } finally {
      setOrderSetSyncing(false);
    }
  }

  async function seedNorthwellCatalog() {
    setNwSeeding(true);
    setNwSeedMsg(null);
    try {
      const { count } = await ccmApi.seedSmsNorthwellCatalog();
      setNorthwellCatalogCount(count);
      setNwSeedMsg(`Seeded ${count} Northwell test${count === 1 ? '' : 's'}.`);
    } catch (e) {
      setNwSeedMsg(`Seed failed: ${e.message}`);
    } finally {
      setNwSeeding(false);
    }
  }

  async function saveDefaults() {
    setDefaultsSaving(true);
    setDefaultsMsg('');
    try {
      const body = {
        defaultMedQty: defaults.defaultMedQty === '' ? null : Number(defaults.defaultMedQty),
        defaultMedRefills: defaults.defaultMedRefills === '' ? null : Number(defaults.defaultMedRefills),
        applyDefaultQtyToRefills: !!defaults.applyDefaultQtyToRefills,
        applyDefaultRefillsToRefills: !!defaults.applyDefaultRefillsToRefills,
      };
      await ccmApi.setSmsDefaults(body);
      setDefaultsMsg('Saved.');
    } catch (e) {
      setDefaultsMsg(`Save failed: ${e.message || 'unknown error'}`);
    } finally {
      setDefaultsSaving(false);
    }
  }

  async function saveSpecimenJurisdiction(code) {
    setJurisdictionSaving(true);
    setJurisdictionMsg('');
    try {
      await ccmApi.patchSmsConfig({ labSpecimenJurisdiction: code || null });
      setSpecimenJurisdiction(code);
      setJurisdictionMsg('Saved.');
    } catch (e) {
      setJurisdictionMsg(`Save failed: ${e.message || 'unknown error'}`);
    } finally {
      setJurisdictionSaving(false);
    }
  }

  async function saveLabVendor(compendiumId) {
    setLabVendorSaving(true);
    setLabVendorMsg('');
    try {
      await ccmApi.patchSmsConfig({ defaultLabVendor: compendiumId || null });
      setDefaultLabVendor(compendiumId);
      setLabVendorMsg('Saved.');
    } catch (e) {
      setLabVendorMsg(`Save failed: ${e.message || 'unknown error'}`);
    } finally {
      setLabVendorSaving(false);
    }
  }

  if (error) return <p style={{ color: 'red' }}>{error}</p>;
  if (!data) return <p style={{ color: '#888' }}>Loading…</p>;

  return (
    <div style={{ maxWidth: 720 }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
        padding: '10px 14px', background: '#f0f9ff', border: '1px solid #bae6fd',
        borderRadius: 8, marginBottom: 14,
      }}>
        <div style={{ flex: 1, minWidth: 240, fontSize: 13, color: '#0c4a6e' }}>
          <strong>Patient directory</strong> — lets the bot match any Elation patient,
          not just enrolled ones. Refresh after new patients are added in Elation.
        </div>
        <button className="btn" onClick={syncDirectory} disabled={syncing}>
          {syncing ? 'Syncing…' : 'Sync patient directory'}
        </button>
      </div>
      {syncMsg && (
        <p style={{ fontSize: 13, color: syncMsg.startsWith('Sync failed') ? '#b91c1c' : '#1a7f4b', marginTop: -4 }}>
          {syncMsg}
        </p>
      )}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
        padding: '10px 14px', background: '#f0f9ff', border: '1px solid #bae6fd',
        borderRadius: 8, marginBottom: 14,
      }}>
        <div style={{ flex: 1, minWidth: 240, fontSize: 13, color: '#0c4a6e' }}>
          <strong>Lab catalog</strong> — the test menus the bot matches against,
          for every lab this practice has connected in Elation. Run once after
          setup, and again whenever a lab is added or updates its compendium.
          <br />
          <em>Keep this tab open until it finishes</em> — it pages through
          Elation 100 tests at a time, so a large new lab is hundreds of rounds,
          and closing the tab strands the run and blocks the automatic sync for
          15 minutes.
        </div>
        <button className="btn" onClick={syncLabCatalog} disabled={labSyncing}>
          {labSyncing ? 'Syncing…' : 'Sync lab catalog'}
        </button>
      </div>
      {labSyncMsg && (
        <p style={{ fontSize: 13, color: labSyncMsg.startsWith('Sync failed') ? '#b91c1c' : '#1a7f4b', marginTop: -4 }}>
          {labSyncMsg}
        </p>
      )}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
        padding: '10px 14px', background: '#f0f9ff', border: '1px solid #bae6fd',
        borderRadius: 8, marginBottom: 14,
      }}>
        <div style={{ flex: 1, minWidth: 240, fontSize: 13, color: '#0c4a6e' }}>
          <strong>Lab order sets</strong> — saved bundles from Elation
          Settings → Lab Orders (e.g. "Anemia Workup", "STD Screening").
          Independent from the catalog above — quick to run; doesn't touch
          the test menus.
        </div>
        <button className="btn" onClick={syncLabOrderSets} disabled={orderSetSyncing}>
          {orderSetSyncing ? 'Syncing…' : 'Sync order sets'}
        </button>
      </div>
      {orderSetSyncMsg && (
        <p style={{ fontSize: 13, color: orderSetSyncMsg.startsWith('Sync failed') ? '#b91c1c' : '#1a7f4b', marginTop: -4 }}>
          {orderSetSyncMsg}
        </p>
      )}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
        padding: '10px 14px', background: '#f0fdfa', border: '1px solid #99f6e4',
        borderRadius: 8, marginBottom: 14,
      }}>
        <div style={{ flex: 1, minWidth: 240, fontSize: 13, color: '#115e59' }}>
          <strong>Northwell catalog</strong> — Northwell Health Lab's test list for
          printed orders. Sourced from Northwell directly, because it has no
          Elation compendium; separate from the lab catalog above, and not needed
          by a lab that Elation connects. Run once after setup.
          {typeof northwellCatalogCount === 'number' && ` ${northwellCatalogCount} test${northwellCatalogCount === 1 ? '' : 's'} loaded.`}
        </div>
        <button className="btn" onClick={seedNorthwellCatalog} disabled={nwSeeding}>
          {nwSeeding ? 'Seeding…' : 'Seed Northwell catalog'}
        </button>
      </div>
      {nwSeedMsg && (
        <p style={{ fontSize: 13, color: nwSeedMsg.startsWith('Seed failed') ? '#b91c1c' : '#1a7f4b', marginTop: -4 }}>
          {nwSeedMsg}
        </p>
      )}
      <div style={{
        padding: '12px 14px', background: '#fefce8', border: '1px solid #fde68a',
        borderRadius: 8, marginBottom: 14,
      }}>
        <div style={{ fontSize: 13, color: '#713f12', marginBottom: 10 }}>
          <strong>Medication order defaults</strong> — fills in quantity and refills
          when the provider doesn't dictate them. Leave blank to use the current
          fallback (qty = frequency × 30 days, refills = 0).
        </div>
        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'center' }}>
          <label style={{ fontSize: 13, color: '#713f12' }}>
            Default qty&nbsp;
            <input
              type="number" min="1" max="999"
              value={defaults.defaultMedQty}
              onChange={e => setDefaults({ ...defaults, defaultMedQty: e.target.value })}
              style={{ width: 70 }}
            />
          </label>
          <label style={{ fontSize: 13, color: '#713f12' }}>
            Default refills&nbsp;
            <input
              type="number" min="0" max="11"
              value={defaults.defaultMedRefills}
              onChange={e => setDefaults({ ...defaults, defaultMedRefills: e.target.value })}
              style={{ width: 70 }}
            />
          </label>
        </div>
        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginTop: 10 }}>
          <label style={{ fontSize: 13, color: '#713f12' }}>
            <input
              type="checkbox"
              checked={defaults.applyDefaultQtyToRefills}
              onChange={e => setDefaults({ ...defaults, applyDefaultQtyToRefills: e.target.checked })}
            />&nbsp;Apply default qty to refills too
          </label>
          <label style={{ fontSize: 13, color: '#713f12' }}>
            <input
              type="checkbox"
              checked={defaults.applyDefaultRefillsToRefills}
              onChange={e => setDefaults({ ...defaults, applyDefaultRefillsToRefills: e.target.checked })}
            />&nbsp;Apply default refills count to refills too
          </label>
        </div>
        <div style={{ marginTop: 10 }}>
          <button className="btn" onClick={saveDefaults} disabled={defaultsSaving}>
            {defaultsSaving ? 'Saving…' : 'Save defaults'}
          </button>
          {defaultsMsg && (
            <span style={{ marginLeft: 12, fontSize: 13, color: defaultsMsg.startsWith('Save failed') ? '#b91c1c' : '#1a7f4b' }}>
              {defaultsMsg}
            </span>
          )}
        </div>
      </div>
      {recognizedVendors.length > 0 && (
        <div style={{
          padding: '12px 14px', background: '#f0fdf4', border: '1px solid #86efac',
          borderRadius: 8, marginBottom: 14,
        }}>
          <div style={{ fontSize: 13, color: '#14532d', marginBottom: 10 }}>
            <strong>Default lab vendor</strong> — used when the provider doesn't name
            a vendor. Leave blank to let the provider pick in Elation.
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <select
              value={defaultLabVendor}
              disabled={labVendorSaving}
              onChange={e => saveLabVendor(e.target.value)}
              style={{ padding: '6px 8px', borderRadius: 6, border: '1px solid #d1d5db', fontSize: 13, minWidth: 220 }}
            >
              <option value="">— None (provider picks in Elation) —</option>
              {recognizedVendors.map(v => (
                <option key={v.compendiumId} value={v.compendiumId}>{v.vendorName}</option>
              ))}
            </select>
            {labVendorSaving && <span style={{ fontSize: 13, color: '#555' }}>Saving…</span>}
            {labVendorMsg && (
              <span style={{ fontSize: 13, color: labVendorMsg.startsWith('Save failed') ? '#b91c1c' : '#1a7f4b' }}>
                {labVendorMsg}
              </span>
            )}
          </div>
        </div>
      )}
      {jurisdictionOptions.length > 0 && (
        <div style={{
          padding: '12px 14px', background: '#f0fdf4', border: '1px solid #86efac',
          borderRadius: 8, marginBottom: 14,
        }}>
          <div style={{ fontSize: 13, color: '#14532d', marginBottom: 10 }}>
            <strong>Specimen jurisdiction</strong> — the state your specimens are drawn in.
            Lab catalogs carry state-licensed rows ("Vitamin E, NY", "…, NOT NY"): with a state set,
            the bot orders that state's version of a test, never a row the lab refuses for it, and
            lists other states' rows last. Leave blank to ignore jurisdiction. A dictation can
            override it per order ("non-NY", "NJ specimen").
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <select
              value={specimenJurisdiction}
              disabled={jurisdictionSaving}
              onChange={e => saveSpecimenJurisdiction(e.target.value)}
              style={{ padding: '6px 8px', borderRadius: 6, border: '1px solid #d1d5db', fontSize: 13, minWidth: 220 }}
            >
              <option value="">— None (ignore jurisdiction) —</option>
              {jurisdictionOptions.map(o => (
                <option key={o.code} value={o.code}>{o.name} ({o.code})</option>
              ))}
            </select>
            {jurisdictionSaving && <span style={{ fontSize: 13, color: '#555' }}>Saving…</span>}
            {jurisdictionMsg && (
              <span style={{ fontSize: 13, color: jurisdictionMsg.startsWith('Save failed') ? '#b91c1c' : '#1a7f4b' }}>
                {jurisdictionMsg}
              </span>
            )}
          </div>
        </div>
      )}
      <p style={{ fontSize: 13, color: '#555', marginTop: 0 }}>
        The bot attributes each order to the provider's Elation physician. Map each
        CCM user to their Elation physician below. This is required for order
        creation; CCM and Elation logins don't have to share an email.
      </p>
      {data.physicians.length === 0 && (
        <p style={{ color: '#b45309', fontSize: 13 }}>
          No Elation physicians loaded — check the practice's Elation connection.
        </p>
      )}
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
        <thead>
          <tr style={{ borderBottom: '2px solid #e5e7eb', textAlign: 'left' }}>
            <th style={{ padding: '8px 10px' }}>CCM User</th>
            <th style={{ padding: '8px 10px' }}>Role</th>
            <th style={{ padding: '8px 10px' }}>SMS phone</th>
            <th style={{ padding: '8px 10px' }}>Elation Physician</th>
          </tr>
        </thead>
        <tbody>
          {data.users.map(u => (
            <tr key={u.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
              <td style={{ padding: '8px 10px' }}>
                {u.displayName}<br />
                <span style={{ fontSize: 12, color: '#888' }}>{u.email}</span>
              </td>
              <td style={{ padding: '8px 10px', color: '#555' }}>{u.role}</td>
              <td style={{ padding: '8px 10px' }}>
                <input
                  type="tel"
                  placeholder="+15551234567"
                  value={phoneDrafts[String(u.id)] ?? ''}
                  disabled={phoneSavingId === u.id}
                  onChange={e => setPhoneDrafts({ ...phoneDrafts, [String(u.id)]: e.target.value })}
                  style={{ width: 150, padding: '6px 8px', borderRadius: 6, border: '1px solid #d1d5db', fontSize: 13 }}
                />
                <button
                  className="btn"
                  onClick={() => savePhone(u)}
                  disabled={phoneSavingId === u.id}
                  style={{ marginLeft: 6 }}
                >
                  {phoneSavingId === u.id ? '…' : 'Save'}
                </button>
                {phoneMsgs[String(u.id)] && (
                  <div style={{ fontSize: 12, marginTop: 4, color: phoneMsgs[String(u.id)].isError ? '#b91c1c' : '#1a7f4b' }}>
                    {phoneMsgs[String(u.id)].text}
                  </div>
                )}
              </td>
              <td style={{ padding: '8px 10px' }}>
                <select
                  value={u.elationUserId || ''}
                  disabled={savingId === u.id}
                  onChange={e => setMapping(u.id, e.target.value)}
                  style={{ padding: '6px 8px', borderRadius: 6, border: '1px solid #d1d5db', fontSize: 13, minWidth: 200 }}
                >
                  <option value="">— Not mapped —</option>
                  {data.physicians.map(p => (
                    <option key={p.id} value={p.id}>
                      {p.name}{p.npi ? ` (NPI ${p.npi})` : ''}
                    </option>
                  ))}
                </select>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function SmsBotPage({ onClose, user }) {
  const [sessions, setSessions] = useState(null);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(null);
  const [tab, setTab] = useState('activity'); // 'activity' | 'demo' | 'providers'

  const load = useCallback(async () => {
    try {
      const data = await ccmApi.listSmsActivity();
      setSessions(data);
      setError(null);
    } catch (e) {
      setError(e.message);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const isAdmin = user?.role === 'admin';

  return (
    <div style={{
      position: 'fixed', inset: 0, background: '#fff', zIndex: 200,
      display: 'flex', flexDirection: 'column', overflowY: 'auto',
    }}>
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '14px 20px', borderBottom: '1px solid #e5e7eb',
        position: 'sticky', top: 0, background: '#fff', zIndex: 1,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <h2 style={{ margin: 0, fontSize: 18 }}>SMS Bot</h2>
          {isAdmin && (
            <div style={{ display: 'flex', gap: 4 }}>
              <button
                className="btn"
                onClick={() => setTab('activity')}
                style={{ fontSize: 13, fontWeight: tab === 'activity' ? 700 : 400, opacity: tab === 'activity' ? 1 : 0.6 }}
              >Activity</button>
              <button
                className="btn"
                onClick={() => setTab('demo')}
                style={{ fontSize: 13, fontWeight: tab === 'demo' ? 700 : 400, opacity: tab === 'demo' ? 1 : 0.6 }}
              >Try the bot</button>
              <button
                className="btn"
                onClick={() => setTab('providers')}
                style={{ fontSize: 13, fontWeight: tab === 'providers' ? 700 : 400, opacity: tab === 'providers' ? 1 : 0.6 }}
              >Providers</button>
            </div>
          )}
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          {tab === 'activity' && <button className="btn" onClick={load} style={{ fontSize: 13 }}>Refresh</button>}
          <button className="btn" onClick={onClose}>Close</button>
        </div>
      </div>

      <div style={{ padding: '16px 20px', flex: 1 }}>
        {!isAdmin && (
          <p style={{ color: '#888' }}>Admin access required to view SMS Bot activity.</p>
        )}
        {isAdmin && tab === 'demo' && <DemoChat />}
        {isAdmin && tab === 'providers' && <ProviderMapping />}
        {isAdmin && tab === 'activity' && error && <p style={{ color: 'red' }}>{error}</p>}
        {isAdmin && tab === 'activity' && !sessions && !error && <p style={{ color: '#888' }}>Loading…</p>}
        {isAdmin && tab === 'activity' && sessions && sessions.length === 0 && (
          <p style={{ color: '#888' }}>No SMS bot sessions yet.</p>
        )}
        {isAdmin && tab === 'activity' && sessions && sessions.length > 0 && (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
            <thead>
              <tr style={{ borderBottom: '2px solid #e5e7eb', textAlign: 'left' }}>
                <th style={{ padding: '8px 10px', fontWeight: 600 }}>Time</th>
                <th style={{ padding: '8px 10px', fontWeight: 600 }}>Provider</th>
                <th style={{ padding: '8px 10px', fontWeight: 600 }}>Message</th>
                <th style={{ padding: '8px 10px', fontWeight: 600 }}>Orders</th>
                <th style={{ padding: '8px 10px', fontWeight: 600 }}>Status</th>
              </tr>
            </thead>
            <tbody>
              {sessions.map(s => (
                <tr
                  key={s.id}
                  onClick={() => setSelected(s.id)}
                  style={{
                    borderBottom: '1px solid #f3f4f6',
                    cursor: 'pointer',
                    transition: 'background 0.1s',
                  }}
                  onMouseEnter={e => e.currentTarget.style.background = '#f9fafb'}
                  onMouseLeave={e => e.currentTarget.style.background = ''}
                >
                  <td style={{ padding: '8px 10px', whiteSpace: 'nowrap', color: '#555' }}>
                    {formatTime(s.createdAt)}
                  </td>
                  <td style={{ padding: '8px 10px', fontFamily: 'monospace', fontSize: 12 }}>
                    {s.providerPhone}
                  </td>
                  <td style={{ padding: '8px 10px', color: '#333', maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {s.message}
                  </td>
                  <td style={{ padding: '8px 10px', color: '#555', fontSize: 12 }}>
                    {s.orderIds && s.orderIds.length > 0 ? `${s.orderIds.length} order${s.orderIds.length > 1 ? 's' : ''}` : '—'}
                  </td>
                  <td style={{ padding: '8px 10px' }}>
                    <span style={{
                      fontSize: 12, fontWeight: 600,
                      color: STATE_COLORS[s.state] || '#333',
                    }}>
                      {STATE_LABELS[s.state] || s.state}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {selected && (
        <SessionDetailModal
          sessionId={selected}
          onClose={() => setSelected(null)}
        />
      )}
    </div>
  );
}
